import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type FocusEvent,
    type ForwardedRef,
    type KeyboardEvent,
    type MouseEvent,
    type MutableRefObject,
    type PointerEvent,
    type Ref,
} from 'react';
import type { Options, OptionProp, OptionsGroup } from '../../shared/normalize-options';
import { useHiddenInputController } from './use-hidden-input-controller';
import { useTypeaheadOptions } from './use-typeahead-options';
import { useTypeaheadFiltering, type TypeaheadMatchFn } from './use-typeahead-filtering';
import { useTypeaheadNavigation, type UseTypeaheadNavigationResult } from './use-typeahead-navigation';
import { useTypeaheadKeyboard } from './use-typeahead-keyboard';
import { optionDomId, useDropdownFocusRing } from './use-dropdown-focus-ring';
import { defaultDomEnvironment } from '../dom-environment';
import type {
    DomEnvironment,
    DropdownCloseStrategy,
    TypeaheadDropdownApi,
    TypeaheadInteractionContext,
    TypeaheadInteractionMode,
    TypeaheadValidationReason,
    TypeaheadValidationResult,
    TypeaheadValidationState,
    TypeaheadValidationStrategy,
} from '../types';

type ValidationAdapter = (value: string, context: { options: OptionProp[]; selectedOption?: OptionProp }) => boolean;

type UseTypeaheadControllerParams = {
    forwardedRef: ForwardedRef<HTMLInputElement>;
    options: Options;
    value?: string;
    matchOption?: TypeaheadMatchFn;
    preserveSelectedOption?: boolean;
    onInputValueChange?: (value: string) => void;
    onHiddenInputChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    validationStrategy?: TypeaheadValidationStrategy;
    legacyValidationAdapter?: ValidationAdapter;
    dropdownCloseStrategy?: DropdownCloseStrategy;
    legacyCloseOnSelect?: boolean | ((option: OptionProp) => boolean);
    onDropdownToggle?: (isOpen: boolean) => void;
    onReset?: () => void;
    externalInputRef?: Ref<HTMLInputElement>;
    dropdownApiRef?: MutableRefObject<TypeaheadDropdownApi | null>;
    openOnFocus?: boolean;
    shouldAutoOpen?: (value: string) => boolean;
    isValidProp?: boolean;
    environment?: DomEnvironment;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    wrapperRef?: MutableRefObject<HTMLElement | null>;
    onValidationStateChange?: (state: TypeaheadValidationState) => void;
    interactionContext?: TypeaheadInteractionContext;
};

type TypeaheadControllerDropdownModel = {
    listboxId: string;
    query: string;
    groups: OptionsGroup;
    flattenedOptions: OptionProp[];
    optionIndexByValue: Map<string, number>;
    activeIndex: number;
    selectedOption?: OptionProp;
};

type TypeaheadControllerHandlers = {
    handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleInputFocus: (event: FocusEvent<HTMLInputElement>) => void;
    handleInputBlur: (event: FocusEvent<HTMLInputElement>) => void;
    handleInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    handleOptionPointerDown: (option: OptionProp) => (event: MouseEvent | PointerEvent) => void;
    handleOptionHover: (index: number) => void;
    handleOptionFocus: (index: number) => void;
    handleOptionKeyDown: (option: OptionProp, index: number) => (event: KeyboardEvent<HTMLLIElement>) => void;
    handleReset: () => void;
};

type TypeaheadControllerHiddenInput = {
    hiddenInputRef: MutableRefObject<HTMLInputElement | null>;
    handleHiddenChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type TypeaheadControllerState = {
    inputValue: string;
    isDropdownOpen: boolean;
    validation: TypeaheadValidationState;
    activeOptionId?: string;
};

type TypeaheadControllerViewModel = {
    dropdown: TypeaheadControllerDropdownModel;
    handlers: TypeaheadControllerHandlers;
    hiddenInput: TypeaheadControllerHiddenInput;
    state: TypeaheadControllerState;
    displayInputRef: MutableRefObject<HTMLInputElement | null>;
    mergeDisplayInputRef: (node: HTMLInputElement | null) => void;
    runValidation: (value: string, reason?: TypeaheadValidationReason) => void;
    closeDropdown: () => void;
    openDropdown: () => void;
    navigation: UseTypeaheadNavigationResult;
};

let listboxIdCounter = 0;
const createListboxId = () => `typeahead-options-${listboxIdCounter++}`;

const defaultValidationStrategy: TypeaheadValidationStrategy = (value, context): TypeaheadValidationResult => {
    const trimmed = value.trim();
    if (!trimmed) {
        return { status: 'pristine', hiddenValue: '' };
    }
    const lowered = trimmed.toLowerCase();
    const matched = context.options.find((option) => {
        return option.value.toLowerCase() === lowered || option.label.toLowerCase() === lowered;
    });
    if (matched) {
        return { status: 'valid', hiddenValue: matched.value, option: matched };
    }
    return { status: 'invalid', hiddenValue: '' };
};

const adaptLegacyValidation = (legacy?: ValidationAdapter): TypeaheadValidationStrategy | undefined => {
    if (!legacy) return undefined;
    return (value, context) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return { status: 'pristine', hiddenValue: '' };
        }
        const isValid = legacy(trimmed, context);
        if (isValid) {
            const hiddenValue = context.selectedOption?.value ?? trimmed;
            return { status: 'valid', hiddenValue, option: context.selectedOption };
        }
        return { status: 'invalid', hiddenValue: '' };
    };
};

const defaultDropdownCloseStrategy: DropdownCloseStrategy = () => 'close';

const adaptLegacyCloseOnSelect = (legacy?: boolean | ((option: OptionProp) => boolean)): DropdownCloseStrategy | undefined => {
    if (typeof legacy === 'boolean') {
        return () => (legacy ? 'close' : 'keep-open');
    }
    if (typeof legacy === 'function') {
        return ({ option }) => (legacy(option) ? 'close' : 'keep-open');
    }
    return undefined;
};

const resolveValidationStrategy = ({
    validationStrategy,
    legacyValidationAdapter,
}: {
    validationStrategy?: TypeaheadValidationStrategy;
    legacyValidationAdapter?: ValidationAdapter;
}): TypeaheadValidationStrategy => {
    return validationStrategy ?? adaptLegacyValidation(legacyValidationAdapter) ?? defaultValidationStrategy;
};

const resolveDropdownCloseStrategy = ({
    dropdownCloseStrategy,
    legacyCloseOnSelect,
}: {
    dropdownCloseStrategy?: DropdownCloseStrategy;
    legacyCloseOnSelect?: boolean | ((option: OptionProp) => boolean);
}): DropdownCloseStrategy => {
    return dropdownCloseStrategy ?? adaptLegacyCloseOnSelect(legacyCloseOnSelect) ?? defaultDropdownCloseStrategy;
};

const useTypeaheadController = ({
    forwardedRef,
    options,
    value,
    matchOption,
    preserveSelectedOption,
    onInputValueChange,
    onHiddenInputChange,
    validationStrategy: providedValidationStrategy,
    legacyValidationAdapter,
    dropdownCloseStrategy: providedDropdownCloseStrategy,
    legacyCloseOnSelect,
    onDropdownToggle,
    onReset,
    externalInputRef,
    dropdownApiRef,
    openOnFocus = true,
    shouldAutoOpen,
    isValidProp,
    environment = defaultDomEnvironment,
    onFocus,
    onBlur,
    wrapperRef,
    onValidationStateChange,
    interactionContext,
}: UseTypeaheadControllerParams): TypeaheadControllerViewModel => {
    const listboxId = useMemo(() => createListboxId(), []);
    const displayInputRef = useRef<HTMLInputElement | null>(null);

    const hiddenInputController = useHiddenInputController({ forwardedRef, onChange: onHiddenInputChange });
    const { hiddenInputRef, handleHiddenChange, setHiddenValue, manualValueRef, lastModeRef, lastValueRef } = hiddenInputController;

    const optionsModel = useTypeaheadOptions({ options, value, preserveSelectedOption });
    const filtering = useTypeaheadFiltering({ groups: optionsModel.normalizedGroups, matchOption });
    const navigation = useTypeaheadNavigation({ options: filtering.flattenedVisibleOptions });

    const isDropdownOpen = filtering.isOpen;

    const interactionModeRef = useRef<TypeaheadInteractionMode>('idle');
    const [validationState, setValidationState] = useState<TypeaheadValidationState>(() => ({
        status: typeof isValidProp === 'boolean' ? (isValidProp ? 'valid' : 'invalid') : 'pristine',
        reason: 'initial',
        dirty: typeof isValidProp === 'boolean' ? true : undefined,
        interaction: interactionModeRef.current,
    }));

    const setInteractionMode = useCallback(
        (mode: TypeaheadInteractionMode) => {
            interactionModeRef.current = mode;
            setValidationState((previous) => {
                if (previous.interaction === mode) {
                    return previous;
                }
                return { ...previous, interaction: mode };
            });
        },
        [setValidationState]
    );

    const assignValidationState = useCallback(
        ({
            status,
            reason,
            dirty,
            interaction,
        }: {
            status: TypeaheadValidationState['status'];
            reason: TypeaheadValidationReason;
            dirty: TypeaheadValidationState['dirty'];
            interaction?: TypeaheadInteractionMode;
        }) => {
            setValidationState((previous) => {
                const next: TypeaheadValidationState = {
                    status,
                    reason,
                    dirty,
                    interaction: interaction ?? interactionModeRef.current,
                };
                if (previous.status === next.status && previous.reason === next.reason && previous.dirty === next.dirty && previous.interaction === next.interaction) {
                    return previous;
                }
                return next;
            });
        },
        [setValidationState]
    );

    const validationStrategy = useMemo(
        () =>
            resolveValidationStrategy({
                validationStrategy: providedValidationStrategy,
                legacyValidationAdapter,
            }),
        [legacyValidationAdapter, providedValidationStrategy]
    );

    const dropdownCloseStrategy = useMemo(
        () =>
            resolveDropdownCloseStrategy({
                dropdownCloseStrategy: providedDropdownCloseStrategy,
                legacyCloseOnSelect,
            }),
        [legacyCloseOnSelect, providedDropdownCloseStrategy]
    );

    const clearManualHiddenValue = useCallback(() => {
        manualValueRef.current = undefined;
        if (lastModeRef.current === 'manual') {
            lastModeRef.current = 'derived';
        }
    }, [lastModeRef, manualValueRef]);

    const applyHiddenValue = useCallback(
        (next: string, mode: 'manual' | 'derived') => {
            setHiddenValue(next, mode);
        },
        [setHiddenValue]
    );

    const derivedHiddenValue = value ?? optionsModel.selectedOption?.value ?? manualValueRef.current ?? '';

    useEffect(() => {
        const manualValue = manualValueRef.current;
        if (manualValue !== undefined && manualValue === derivedHiddenValue) {
            return;
        }
        if (lastValueRef.current === derivedHiddenValue && lastModeRef.current === 'derived') {
            return;
        }
        applyHiddenValue(derivedHiddenValue, 'derived');
    }, [applyHiddenValue, derivedHiddenValue, lastModeRef, lastValueRef, manualValueRef]);

    useEffect(() => {
        if (typeof value !== 'undefined') {
            clearManualHiddenValue();
        }
    }, [clearManualHiddenValue, value]);

    const activeOptionId = navigation.activeIndex >= 0 ? optionDomId(listboxId, navigation.activeIndex) : undefined;

    const runValidation = useCallback(
        (displayValue: string, reason: TypeaheadValidationReason = 'input') => {
            const trimmed = displayValue.trim();
            if (!trimmed) {
                optionsModel.clearSelection();
                applyHiddenValue('', 'derived');
                assignValidationState({ status: 'pristine', reason, dirty: false, interaction: 'programmatic' });
                return;
            }

            const result = validationStrategy(trimmed, {
                options: optionsModel.flattenedOptions,
                selectedOption: optionsModel.selectedOption,
            });

            if (result.status === 'pristine') {
                optionsModel.clearSelection();
                applyHiddenValue(result.hiddenValue ?? '', 'derived');
                assignValidationState({ status: 'pristine', reason, dirty: false, interaction: 'programmatic' });
                return;
            }

            if (result.status === 'valid') {
                const option = result.option;
                if (option) {
                    optionsModel.selectOption(option);
                    filtering.setInputValue(option.label);
                    navigation.setActiveIndex(optionsModel.optionIndexByValue.get(option.value) ?? -1);
                }
                applyHiddenValue(result.hiddenValue, 'manual');
                assignValidationState({ status: 'valid', reason, dirty: true });
                return;
            }

            optionsModel.clearSelection();
            applyHiddenValue(result.hiddenValue ?? '', 'derived');
            assignValidationState({ status: 'invalid', reason, dirty: true });
        },
        [applyHiddenValue, assignValidationState, filtering, navigation, optionsModel, validationStrategy]
    );

    const handleDropdownCloseDecision = useCallback(
        (option: OptionProp) => {
            const decision = dropdownCloseStrategy({ option, context: { selectedOption: optionsModel.selectedOption } });
            return decision === 'close';
        },
        [dropdownCloseStrategy, optionsModel.selectedOption]
    );

    const collapseCaretToEnd = useCallback(() => {
        const node = displayInputRef.current;
        if (!node) return;
        const length = node.value.length;
        try {
            node.setSelectionRange(length, length);
        } catch (error) {
            // Some input types do not support selection ranges; ignore in that case.
        }
    }, []);

    const focusAndCollapseCaret = useCallback(() => {
        const node = displayInputRef.current;
        if (!node) return;
        node.focus();
        collapseCaretToEnd();
    }, [collapseCaretToEnd]);

    const suppressOpenOnFocusRef = useRef(false);

    const commitOption = useCallback(
        (option: OptionProp) => {
            optionsModel.selectOption(option);
            filtering.setInputValue(option.label);
            applyHiddenValue(option.value, 'derived');
            const shouldClose = handleDropdownCloseDecision(option);
            if (shouldClose) {
                const meta = option.meta as { kind?: string } | undefined;
                suppressOpenOnFocusRef.current = meta?.kind !== 'custom';
                filtering.close();
                assignValidationState({ status: 'valid', reason: 'commit', dirty: true });
            } else {
                suppressOpenOnFocusRef.current = false;
                filtering.open();
                assignValidationState({ status: 'pristine', reason: 'commit', dirty: true });
            }
            interactionContext?.onCommit(option);
            if (shouldClose) {
                interactionContext?.onInteractionEnd();
                setInteractionMode('idle');
            }
            navigation.setActiveIndex(-1);
            navigation.requestOptionFocus(-1);
            setTimeout(() => focusAndCollapseCaret(), 0);
        },
        [applyHiddenValue, assignValidationState, filtering, focusAndCollapseCaret, handleDropdownCloseDecision, interactionContext, navigation, optionsModel, setInteractionMode]
    );

    const handleInputFocus = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (openOnFocus && !suppressOpenOnFocusRef.current) {
                filtering.open();
                const selected = optionsModel.selectedOption;
                if (selected) {
                    const selectedMeta = selected.meta as { kind?: string } | undefined;
                    if (selectedMeta?.kind === 'custom') {
                        setTimeout(() => {
                            navigation.setActiveIndex(-1);
                            navigation.requestOptionFocus(-1);
                        }, 0);
                    } else {
                        const nextIndex = filtering.flattenedVisibleOptions.findIndex((option) => option.value === selected.value && !option.disabled);
                        navigation.setActiveIndex(nextIndex);
                    }
                }
            } else if (suppressOpenOnFocusRef.current) {
                suppressOpenOnFocusRef.current = false;
            }
            onFocus?.(event);
        },
        [filtering, navigation, onFocus, openOnFocus, optionsModel.selectedOption]
    );

    const handleInputBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const relatedTarget = event.relatedTarget;
            const wrapperElement = wrapperRef?.current;
            if (relatedTarget instanceof HTMLElement && wrapperElement?.contains(relatedTarget)) {
                return;
            }
            onBlur?.(event);
            filtering.close();
            setInteractionMode('programmatic');
            runValidation(event.currentTarget.value, 'blur');
            interactionContext?.onInteractionEnd();
            setInteractionMode('idle');
        },
        [filtering, interactionContext, onBlur, runValidation, setInteractionMode, wrapperRef]
    );

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            interactionContext?.onInteractionStart('keyboard');
            setInteractionMode('keyboard');
            filtering.setInputValue(newValue);
            const shouldOpenDropdown = shouldAutoOpen ? shouldAutoOpen(newValue) : true;
            if (shouldOpenDropdown) {
                filtering.open();
            } else {
                filtering.close();
            }
            optionsModel.clearSelection();
            navigation.setActiveIndex(-1);
            navigation.requestOptionFocus(-1);
            assignValidationState({
                status: 'pristine',
                reason: 'input',
                dirty: newValue ? true : false,
            });
            interactionContext?.onInputChange(newValue);
            if (manualValueRef.current !== undefined) {
                clearManualHiddenValue();
            }
            onInputValueChange?.(newValue);
        },
        [
            assignValidationState,
            clearManualHiddenValue,
            filtering,
            interactionContext,
            manualValueRef,
            navigation,
            onInputValueChange,
            optionsModel,
            setInteractionMode,
            shouldAutoOpen,
        ]
    );

    const handleReset = useCallback(() => {
        displayInputRef.current?.focus();
        filtering.reset();
        filtering.open();
        optionsModel.clearSelection();
        navigation.setActiveIndex(-1);
        setInteractionMode('programmatic');
        assignValidationState({
            status: 'pristine',
            reason: 'reset',
            dirty: undefined,
            interaction: 'programmatic',
        });
        applyHiddenValue('', 'derived');
        onInputValueChange?.('');
        onReset?.();
        interactionContext?.onInteractionEnd();
        setInteractionMode('idle');
    }, [applyHiddenValue, assignValidationState, filtering, interactionContext, navigation, onInputValueChange, onReset, optionsModel, setInteractionMode]);

    const handleOptionPointerDown = useCallback(
        (option: OptionProp) => (event: MouseEvent | PointerEvent) => {
            event.preventDefault();
            if (option.disabled) return;
            interactionContext?.onInteractionStart('pointer');
            setInteractionMode('pointer');
            commitOption(option);
        },
        [commitOption, interactionContext, setInteractionMode]
    );

    const handleOptionHover = useCallback(
        (index: number) => {
            navigation.setActiveIndex(index);
        },
        [navigation]
    );

    const handleOptionFocus = useCallback(
        (index: number) => {
            navigation.setActiveIndex(index);
        },
        [navigation]
    );

    const commitOptionWithKeyboard = useCallback(
        (option: OptionProp) => {
            interactionContext?.onInteractionStart('keyboard');
            setInteractionMode('keyboard');
            commitOption(option);
        },
        [commitOption, interactionContext, setInteractionMode]
    );

    const { handleInputKeyDown, handleOptionKeyDown } = useTypeaheadKeyboard({
        navigation,
        filtering,
        commitOption: commitOptionWithKeyboard,
        focusInput: focusAndCollapseCaret,
    });

    useEffect(() => {
        if (typeof isValidProp === 'undefined') {
            assignValidationState({
                status: 'pristine',
                reason: 'external',
                dirty: validationState.dirty,
            });
            return;
        }
        assignValidationState({
            status: isValidProp ? 'valid' : 'invalid',
            reason: 'external',
            dirty: validationState.dirty ?? true,
        });
    }, [assignValidationState, isValidProp, validationState.dirty]);

    useEffect(() => {
        onValidationStateChange?.(validationState);
    }, [onValidationStateChange, validationState]);

    const previousValueRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (value === previousValueRef.current) return;
        previousValueRef.current = value;

        if (typeof value === 'undefined') {
            return;
        }

        const matched = optionsModel.findOptionByValue(value);
        const displayValue = matched ? matched.label : value;
        filtering.setInputValue(displayValue);
        if (matched) {
            assignValidationState({
                status: 'valid',
                reason: 'external',
                dirty: true,
            });
        } else if (!displayValue) {
            assignValidationState({
                status: 'pristine',
                reason: 'external',
                dirty: undefined,
            });
        } else {
            assignValidationState({
                status: 'pristine',
                reason: 'external',
                dirty: true,
            });
        }
    }, [assignValidationState, filtering, optionsModel, value]);

    const wasOpenRef = useRef(isDropdownOpen);
    useEffect(() => {
        if (isDropdownOpen && !wasOpenRef.current) {
            const selected = optionsModel.selectedOption;
            if (selected) {
                const nextIndex = filtering.flattenedVisibleOptions.findIndex((option) => option.value === selected.value && !option.disabled);
                navigation.setActiveIndex(nextIndex);
            } else {
                navigation.setActiveIndex(-1);
                navigation.requestOptionFocus(-1);
            }
        }
        if (wasOpenRef.current !== isDropdownOpen) {
            onDropdownToggle?.(isDropdownOpen);
        }
        wasOpenRef.current = isDropdownOpen;
    }, [filtering.flattenedVisibleOptions, isDropdownOpen, navigation, onDropdownToggle, optionsModel.selectedOption]);

    useDropdownFocusRing({
        environment,
        inputRef: displayInputRef,
        isOpen: isDropdownOpen,
        listboxId,
        navigation,
    });

    const mergeDisplayInputRef = useCallback(
        (node: HTMLInputElement | null) => {
            displayInputRef.current = node;
            if (!externalInputRef) return;
            if (typeof externalInputRef === 'function') {
                externalInputRef(node);
            } else {
                (externalInputRef as MutableRefObject<HTMLInputElement | null>).current = node;
            }
        },
        [externalInputRef]
    );

    useEffect(() => {
        if (!dropdownApiRef) return;
        dropdownApiRef.current = {
            open: () => filtering.open(),
            close: () => filtering.close(),
        };
        return () => {
            if (dropdownApiRef) {
                dropdownApiRef.current = null;
            }
        };
    }, [dropdownApiRef, filtering]);

    const dropdownModel: TypeaheadControllerDropdownModel = useMemo(
        () => ({
            listboxId,
            query: filtering.inputValue,
            groups: filtering.visibleGroups,
            flattenedOptions: filtering.flattenedVisibleOptions,
            optionIndexByValue: optionsModel.optionIndexByValue,
            activeIndex: navigation.activeIndex,
            selectedOption: optionsModel.selectedOption,
        }),
        [
            filtering.flattenedVisibleOptions,
            filtering.inputValue,
            filtering.visibleGroups,
            listboxId,
            navigation.activeIndex,
            optionsModel.optionIndexByValue,
            optionsModel.selectedOption,
        ]
    );

    return {
        dropdown: dropdownModel,
        handlers: {
            handleInputChange,
            handleInputFocus,
            handleInputBlur,
            handleInputKeyDown,
            handleOptionPointerDown,
            handleOptionHover,
            handleOptionFocus,
            handleOptionKeyDown,
            handleReset,
        },
        hiddenInput: {
            hiddenInputRef,
            handleHiddenChange,
        },
        state: {
            inputValue: filtering.inputValue,
            isDropdownOpen,
            validation: validationState,
            activeOptionId,
        },
        displayInputRef,
        mergeDisplayInputRef,
        runValidation,
        closeDropdown: () => filtering.close(),
        openDropdown: () => filtering.open(),
        navigation,
    };
};

export type {
    TypeaheadControllerDropdownModel,
    TypeaheadControllerHandlers,
    TypeaheadControllerHiddenInput,
    TypeaheadControllerState,
    TypeaheadControllerViewModel,
    UseTypeaheadControllerParams,
};
export { useTypeaheadController };
